import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import TestRunsList from './TestRunsList.tsx';
import Button from '../../../../core/components/Button.tsx';
import Drawer from '../../../../core/components/Drawer.tsx';
import SegmentedButton from '../../../../core/components/SegmentedButton.tsx';
import { UPLOAD_AGENT_FILE } from '../../queries.ts';
import EmlUploadForm from '../records/EmlUploadForm.tsx';
import TestEmailForm from '../records/TestEmailForm.tsx';

type TestMode = 'manual' | 'eml';

interface TestAssistantDrawerProps {
    agentId: string;
    visible: boolean;
    onClose: () => void;
}

/** Test an assistant with an email entered by hand or an .eml file, and see its test runs. */
const TestAssistantDrawer = ({ agentId, visible, onClose }: TestAssistantDrawerProps) => {
    const { t } = useTranslation('assistants');
    const [mode, setMode] = useState<TestMode>('manual');
    const [files, setFiles] = useState<File[]>([]);
    const [isUploading, setUploading] = useState(false);
    const [refetchKey, setRefetchKey] = useState(0);
    const [uploadAgentFile] = useMutation(UPLOAD_AGENT_FILE);

    const refetchRuns = () => setRefetchKey((key) => key + 1);

    // no catch: a failed upload behaves as in the upload modal, which doesn't handle it either
    const handleUpload = async () => {
        if (!files.length) return;
        setUploading(true);
        try {
            await uploadAgentFile({ variables: { agentId, file: files[0] } });
            setFiles([]);
            refetchRuns();
        } finally {
            setUploading(false);
        }
    };

    return (
        <Drawer visible={visible} onClose={onClose} title={t('detail.activity.testAssistant')}>
            <p className="m-0 text-sm text-content-medium">{t('detail.activity.testDrawerDescription')}</p>

            <SegmentedButton value={mode} onValueChange={(value) => setMode(value as TestMode)}>
                <SegmentedButton.Item value="manual">{t('detail.activity.enterManually')}</SegmentedButton.Item>
                <SegmentedButton.Item value="eml">{t('detail.activity.uploadEml')}</SegmentedButton.Item>
            </SegmentedButton>

            {mode === 'manual' ? (
                <TestEmailForm agentId={agentId} onSubmitted={refetchRuns} hasOptionalFieldsCollapsed />
            ) : (
                <div className="flex flex-col gap-4">
                    <EmlUploadForm files={files} onFilesChange={setFiles} />
                    <Button
                        variant="primary"
                        className="self-end"
                        onClick={handleUpload}
                        disabled={!files.length}
                        loading={isUploading}
                    >
                        {t('agentConfig.uploadModal.confirm')}
                    </Button>
                </div>
            )}

            <div className="flex flex-col gap-3">
                <h3 className="m-0 text-base font-semibold text-content-strong">{t('detail.activity.testRuns')}</h3>
                <TestRunsList agentId={agentId} refetchKey={refetchKey} />
            </div>
        </Drawer>
    );
};

export default TestAssistantDrawer;
